"use client";

import { type RefObject, useCallback, useEffect, useState } from "react";

interface UseElementVisibilityOptions {
  root?: RefObject<HTMLElement | null>;
  threshold?: number;
}

export const useElementVisibility = (
  options: UseElementVisibilityOptions = {},
): [boolean, (node: HTMLElement | null) => void] => {
  const [isVisible, setIsVisible] = useState(true);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const { root, threshold = 0 } = options;

  const ref = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: root?.current ?? null,
        threshold,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, root, threshold]);

  return [isVisible, ref];
};
